/**
 * Compliance Matrix Tab Component
 *
 * Table view of all criteria for compliance tracking.
 * Shows gate and scored criteria, plus derived staff & company criteria that score staff/company info.
 */

import React, { useState } from 'react';
import { CheckCircle, Award, Download, Users, Building2 } from 'lucide-react';
import type { ComplianceMatrix } from '../../hooks/useProductionTemplate';

interface ComplianceMatrixTabProps {
  criteria?: any[];
  staffRequirements?: any[];
  companyRequirements?: { capability_statements?: any[]; past_projects_enumeration?: any[] };
}

export function ComplianceMatrixTab({
  criteria = [],
  staffRequirements = [],
  companyRequirements = {},
}: ComplianceMatrixTabProps) {
  const [showGate, setShowGate] = useState(true);
  const [showScored, setShowScored] = useState(true);
  const [showStaffCompany, setShowStaffCompany] = useState(true);

  const capStatements = companyRequirements.capability_statements ?? [];
  const pastProjects = companyRequirements.past_projects_enumeration ?? [];
  const staffCompanyCount = staffRequirements.length + capStatements.length + pastProjects.length;

  // Build compliance matrix from criteria
  const gateCriteria = criteria.filter(c => (c.type || c.kind) === 'gate');
  const scoredCriteria = criteria.filter(c => (c.type || c.kind) === 'scored');
  const totalPoints = scoredCriteria.reduce((sum, c) => sum + (c.points || c.weight || 0), 0);
  const totalCriteria = criteria.length;

  const exportToCSV = () => {
    const rows = [
      ['Criterion ID', 'Type', 'Title', 'Points/Weight', 'Char Pos', 'Compliant (Y/N)', 'Page Reference', 'Comments']
    ];

    if (showGate) {
      gateCriteria.forEach(c => {
        rows.push([
          c.id || c.criterion_id,
          'Gate (Mandatory)',
          c.title,
          'Pass/Fail',
          (c.page || 0).toString(),
          '',
          '',
          ''
        ]);
      });
    }

    if (showScored) {
      scoredCriteria.forEach(c => {
        rows.push([
          c.id || c.criterion_id,
          'Scored',
          c.title,
          (c.points || c.weight || 0).toString(),
          (c.page || 0).toString(),
          '',
          '',
          ''
        ]);
      });
    }

    const csv = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'compliance-matrix.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text">
            Compliance Matrix
          </h3>
          <p className="text-sm text-gray-600 dark:text-theme-text-secondary mt-1">
            Track compliance for {totalCriteria} evaluation criteria ({gateCriteria.length} gate, {scoredCriteria.length} scored, {totalPoints} pts)
            {staffCompanyCount > 0 && ` • ${staffCompanyCount} staff/company items`}
          </p>
        </div>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      {/* Filter Toggles */}
      <div className="flex gap-2">
        <button
          onClick={() => setShowGate(!showGate)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            showGate
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
          }`}
        >
          <CheckCircle className="h-4 w-4 inline mr-1" />
          Gate Criteria ({gateCriteria.length})
        </button>
        <button
          onClick={() => setShowScored(!showScored)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            showScored
              ? 'bg-purple-600 text-white'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
          }`}
        >
          <Award className="h-4 w-4 inline mr-1" />
          Scored Criteria ({scoredCriteria.length})
        </button>
        {staffCompanyCount > 0 && (
          <button
            onClick={() => setShowStaffCompany(!showStaffCompany)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showStaffCompany
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
            }`}
          >
            <Users className="h-4 w-4 inline mr-1" />
            Staff & Company ({staffCompanyCount})
          </button>
        )}
      </div>

      {/* Compliance Table */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Requirement
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Points
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Page
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Compliant
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Page Ref
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Comments
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {/* Gate Criteria */}
              {showGate && gateCriteria.map((criterion) => {
                const criterionId = criterion.id || criterion.criterion_id || '';
                const charPos = criterion.page || 0;
                return (
                <tr key={criterionId} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                      {criterionId}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-theme-text">
                    Gate
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-theme-text max-w-md">
                    <div className="line-clamp-2">{criterion.title || 'Untitled'}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    Pass/Fail
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    @{charPos}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <input
                      type="text"
                      placeholder="p.XX"
                      className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-theme-text"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      placeholder="Add notes..."
                      className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-theme-text"
                    />
                  </td>
                </tr>
              )})}

              {/* Scored Criteria */}
              {showScored && scoredCriteria.map((criterion) => {
                const criterionId = criterion.id || criterion.criterion_id || '';
                const points = criterion.points || criterion.weight || 0;
                const charPos = criterion.page || 0;
                return (
                <tr key={criterionId} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                      {criterionId}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-theme-text">
                    Scored
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-theme-text max-w-md">
                    <div className="line-clamp-2">{criterion.title || 'Untitled'}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-orange-700 dark:text-orange-300">
                      <Award className="h-3 w-3" />
                      {points}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    @{charPos}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <input
                      type="text"
                      placeholder="p.XX"
                      className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-theme-text"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      placeholder="Add notes..."
                      className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-theme-text"
                    />
                  </td>
                </tr>
                );
              })}

              {/* Staff & Company Evaluation (derived criteria that score staff/company info) */}
              {showStaffCompany && staffCompanyCount > 0 && (
                <>
                  {staffRequirements.map((sr: any, idx: number) => (
                    <tr key={`staff-${sr.position_id ?? idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-800 bg-teal-50/50 dark:bg-teal-900/10">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300">
                          {sr.position_id || `S${idx + 1}`}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-theme-text">
                        Staff
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-theme-text max-w-md">
                        <div className="line-clamp-2">{sr.role_title || 'Proposed staff'}</div>
                        {sr.scoring_criteria && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">{String(sr.scoring_criteria).slice(0, 120)}...</div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">—</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">—</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <input type="checkbox" className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded" />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <input type="text" placeholder="p.XX" className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-theme-text" />
                      </td>
                      <td className="px-4 py-3">
                        <input type="text" placeholder="Add notes..." className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-theme-text" />
                      </td>
                    </tr>
                  ))}
                  {capStatements.map((c: any, idx: number) => (
                    <tr key={`cap-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-800 bg-teal-50/50 dark:bg-teal-900/10">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300">
                          Cap {idx + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-theme-text">Company</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-theme-text max-w-md">
                        <div className="line-clamp-2">{c.topic || 'Capability'}</div>
                        {c.scope && <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">{c.scope}</div>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">—</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">—</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <input type="checkbox" className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded" />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <input type="text" placeholder="p.XX" className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-theme-text" />
                      </td>
                      <td className="px-4 py-3">
                        <input type="text" placeholder="Add notes..." className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-theme-text" />
                      </td>
                    </tr>
                  ))}
                  {pastProjects.map((p: any, idx: number) => (
                    <tr key={`past-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-800 bg-teal-50/50 dark:bg-teal-900/10">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300">
                          Past {idx + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-theme-text">Company</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-theme-text max-w-md">
                        <div className="line-clamp-2">Past projects (max {p.max_count ?? 5})</div>
                        {p.criteria && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
                            {[p.criteria.sector, p.criteria.date_range, p.criteria.min_value].filter(Boolean).join(' • ') || '—'}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">—</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">—</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <input type="checkbox" className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded" />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <input type="text" placeholder="p.XX" className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-theme-text" />
                      </td>
                      <td className="px-4 py-3">
                        <input type="text" placeholder="Add notes..." className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-theme-text" />
                      </td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <p className="text-sm text-blue-900 dark:text-blue-300">
          <strong>Instructions:</strong> Fill out compliance status and page references for each criterion as you complete your proposal. Export to CSV to track progress externally.
        </p>
      </div>
    </div>
  );
}
